import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  Notification,
  Logging,
  API,
  NotificationData,
  ResponseError,
  UserInfo,
  UserContext,
  ContentLoading,
  renderCheckbox,
  ExemptionType,
  Footer,
  renderValidationMessage,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import ExemptionTypesProps from './ExemptionTypesProps.interface';
import useExemptionTypes from './useExemptionTypes';
import '../common-ecwizard.scss';
import './exemption-types.scss';

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const ExemptionTypes = ({
  gatewayEndPoint,
}: ExemptionTypesProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(true);

  // Load the Exemption Types
  const [exemptionTypes, exemptionTypesNotification] = useExemptionTypes(gatewayEndPoint, true);

  // User Selected Exemption Values
  const [userExemptionTypes, setUserExemptionTypes] = React.useState(null);

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);

  // Save the Exemption Types
  const saveExemptionTypes = (exemptionTypeIds: number[]) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      Logging.Info('Set Exemption Types:', exemptionTypeIds);

      // Set the Exemption Types
      await api.ecwizard.setExemptionTypesV41(exemptionTypeIds);

      const notificationData: NotificationData = {
        type: 'Information',
        title: 'Exemption Types Saved',
        description: 'The Exemption Types were saved.',
        autoClose: true,
        autoCloseTimeOutMilliseconds: 1000,
        position: 'BottomRight',
      };

      setNotification(notificationData);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Exemption Types Save Error',
        description: 'Unable to Save the Exemption Types.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Save Button was Pressed
  const onSave = (): void => {
    const exemptionTypesUpdated: ExemptionType[] = _.cloneDeep(exemptionTypes);

    let index = 0;
    const exemptionTypeIds: number [] = [];

    // Get the Updated Checkbox Values
    exemptionTypesUpdated.forEach((exemptionType: ExemptionType) => {
      // Add the Exemption Type Ids of the selected items
      if (userExemptionTypes[index] === true) {
        exemptionTypeIds.push(exemptionType.id);
      }

      index += 1;
    });

    dispatch(saveExemptionTypes(exemptionTypeIds));
  };

  // Cancel Button was Pressed
  const onCancel = (): void => {
    if (exemptionTypes) {
      const values: boolean[] = [];

      // Get the Checkbox States
      exemptionTypes.forEach((exemptionType: ExemptionType) => {
        values.push(exemptionType.isUsedByClient !== undefined ? exemptionType.isUsedByClient : false);
      });

      setUserExemptionTypes(values);
    }
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  // Render the Exemption Types
  const renderExemptionTypes = (): JSX.Element[] => {
    let index = -1;

    return exemptionTypes.map((exemptionType: ExemptionType) => {
      index += 1;
      return (
        <div className="field33 exemptionTypeClass" key={`exemption-type-${exemptionType.id}`}>
          {renderCheckbox(`exemption-type-${exemptionType.id}`, exemptionType.name, userExemptionTypes[index], index,
            setUserExemptionTypes, controlStateEditable)}
        </div>
      );
    });
  };

  // Select All Exemption Types
  const onSelectAll = (): void => {
    setUserExemptionTypes(Array(exemptionTypes.length).fill(true));
  };

  // Clear All Exemption Types
  const onClearAll = (): void => {
    setUserExemptionTypes(Array(exemptionTypes.length).fill(false));
  };

  const validateValues = (): boolean => {
    // One Exemption Type must at least be selected
    if ((userExemptionTypes === null) || (userExemptionTypes.filter((x: boolean) => x === true).length === 0)) {
      return false;
    }

    return true;
  };

  // Exemption Types were loaded
  React.useEffect(() => {
    if (exemptionTypes) {
      const values: boolean[] = [];

      // Get the Checkbox States
      exemptionTypes.forEach((exemptionType: ExemptionType) => {
        values.push(exemptionType.isUsedByClient !== undefined ? exemptionType.isUsedByClient : false);
      });

      setUserExemptionTypes(values);

      setIsLoading(false);
    }
  }, [exemptionTypes]);

  // Unable to Load the Exemption Types
  React.useEffect(() => {
    if (exemptionTypesNotification) {
      setIsLoading(false);

      setNotification(exemptionTypesNotification);
    }
  }, [exemptionTypesNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Exemption Types: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setIsLoading(true);
      setNotification(null);
    };
  }, []);

  const canSave: boolean = validateValues();
  const validationMessage = !canSave ? 'At least one Exemption Type must be selected.' : null;
  const selectionInformation = `${userExemptionTypes?.filter((x: boolean) => x === true).length} of ${exemptionTypes?.length} Exemption Types selected`; // eslint-disable-line max-len

  return (
    <>
      {
        isLoading
          ? (
            <ContentLoading loadingMessage="Loading Exemption Types ..." />
          )
          : (
            <div className="ecwizardContainer">
              <div className="ecwizardHeader">
                <div className="ecwizardHeaderLeft">
                  Exemption Types
                </div>
              </div>
              <div className="ecwizardHeaderDescription">
                Choose the Exemption Types.
              </div>
              <hr />
              {
                exemptionTypes && (
                  <>
                    <div className="ecwizardContentControls">
                      <div className="ecwizardContentButtons">
                        <div className="ecwizardContentButtonsLeft">
                          <div className="buttonItemBase">
                            <VtxButton
                              id="selectAllButton"
                              icon={<VtxIcon type="check-square" />}
                              onClick={onSelectAll}
                              type="default"
                            >
                              Select All
                            </VtxButton>
                          </div>
                          <div className="buttonItemBase">
                            <VtxButton
                              id="clearAllButton"
                              icon={<VtxIcon type="border" />}
                              onClick={onClearAll}
                              type="default"
                            >
                              Clear All
                            </VtxButton>
                          </div>
                        </div>
                        <div className="ecwizardContentButtonsRight">
                          <div className="buttonItemBase">
                            {selectionInformation}
                          </div>
                        </div>
                      </div>
                      <div className="ecwizardContentControlsBorder">
                        {renderExemptionTypes()}
                      </div>
                      {renderValidationMessage('exemption-types', validationMessage)}
                    </div>
                    <Footer onCancel={onCancel} saveEnabled={canSave} onSave={onSave} />
                  </>
                )
              }
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default ExemptionTypes;
